import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, LockKeyhole } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/auth.service";
import { safeAuthErrorMessage } from "../utils/safeApiErrorMessage";


const pinInput = "w-full rounded-xl border border-slate-200 px-4 py-3 text-center tracking-[0.5em] focus:outline-none focus:border-[#0A4730]";

export function Security() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ currentPassword: "", currentPin: "", newPin: "", confirmPin: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const statusQuery = useQuery({ queryKey: ["auth", "pin", "status"], queryFn: authService.getPinStatus });
  const mutation = useMutation({
    mutationFn: async () => {
      return statusQuery.data?.configured
        ? authService.changePin({ current_pin: form.currentPin, new_pin: form.newPin })
        : authService.setupPin({ current_password: form.currentPassword, pin: form.newPin });
    },
    onSuccess: () => {
      setError("");
      setMessage("Transaction PIN updated.");
      setForm({ currentPassword: "", currentPin: "", newPin: "", confirmPin: "" });
      queryClient.invalidateQueries({ queryKey: ["auth", "pin", "status"] });
    },
    onError: (err) => {
      setError(safeAuthErrorMessage(err, "PIN could not be updated"));
    },
  });
  const submit = (event) => {
    event.preventDefault();
    setMessage("");
    if (!/^\d{6}$/.test(form.newPin)) {
      setError("PIN must contain exactly 6 digits");
      return;
    }
    if (form.newPin !== form.confirmPin) {
      setError("PINs do not match");
      return;
    }
    setError("");
    mutation.mutate();
  };
  const updatePin = (key) => (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 6);
    setForm((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="min-h-full bg-slate-50 p-4 text-left">
      <div className="max-w-md mx-auto py-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white border border-slate-100 mb-6"><ArrowLeft size={18} /></button>
        <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
          <LockKeyhole className="text-[#0A4730] mb-3" />
          <h1 className="font-serif text-2xl font-semibold text-[#0A4730]">Transaction PIN</h1>
          <p className="text-xs text-slate-500 mt-2 mb-6">Used to authorize in-app and WhatsApp transfers. Five incorrect attempts temporarily lock verification.</p>
          {statusQuery.data?.locked && <p className="mb-4 rounded-xl bg-amber-50 p-3 text-xs text-amber-700">PIN verification is temporarily locked.</p>}
          <form onSubmit={submit} className="space-y-4">
            {statusQuery.data?.configured ? (
              <input type="password" inputMode="numeric" maxLength={6} value={form.currentPin} onChange={updatePin("currentPin")} placeholder="Current PIN" className={pinInput} />
            ) : (
              <input type="password" value={form.currentPassword} onChange={(event) => setForm((current) => ({ ...current, currentPassword: event.target.value }))} placeholder="Current account password" className="w-full rounded-xl border border-slate-200 px-4 py-3" />
            )}
            <input type="password" inputMode="numeric" maxLength={6} value={form.newPin} onChange={updatePin("newPin")} placeholder="New 6-digit PIN" className={pinInput} />
            <input type="password" inputMode="numeric" maxLength={6} value={form.confirmPin} onChange={updatePin("confirmPin")} placeholder="Confirm new PIN" className={pinInput} />
            {error && <p role="alert" className="text-xs text-red-500">{error}</p>}
            {message && <p className="text-xs text-emerald-600">{message}</p>}
            <button disabled={mutation.isPending || statusQuery.isLoading} className="w-full rounded-xl bg-[#0A4730] py-3 text-sm font-semibold text-white disabled:opacity-50">
              {mutation.isPending ? "Saving…" : statusQuery.data?.configured ? "Change PIN" : "Set PIN"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
